import { nextGetUri } from "../../../helpers/next";
import nextRedirect from "../../../helpers/next/redirect";

// constants
import browser from "@splitfire-agency/raiden-library/dist/constants/browser";

// deps
import { PHASE_PRODUCTION_BUILD } from "next/constants";

// hooks
import { serverApiFetcher } from "../../../hooks/useApiFetcher";

// constants
import { DEFAULT_ENVIRONMENT_ID } from "../../../constants/environments";
import { REDIRECTIONS_URLS_TIME_VALUE_PERMANENT } from "../../../constants/redirections/urls";
import generateApiUrl from "../../../libraries/utils/generateApiUrl";

// Matche un point + extension, sauf "html"
const PUBLIC_FILE = /\.(?!html$)[^.]+$/i;

/**
 * @param {import("@splitfire-agency/raiden-library/dist/libraries/Uri").default} baseUri
 * @param {import("next").NextPageContext} [context]
 * @returns {boolean}
 */
function shouldTestUrl(baseUri, context) {
  if (
    context &&
    process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD &&
    !browser
  ) {
    // uniquement en cas de 404
    if (context.res?.statusCode === 404) {
      // on génére l'uri
      const uri = nextGetUri({ req: context?.req });
      if (uri) {
        const pathname = uri.getPath();
        // on exclut de notre test les routes vers /api et les fichiers publiques ainsi que de _next
        if (
          pathname.startsWith("/_next") ||
          pathname.includes("/api/") ||
          PUBLIC_FILE.test(pathname)
        ) {
          return false;
        }

        // on vérifie si l'host est le même que celui de l'uri
        return baseUri.getHost() === uri.getHost();
      }
    }
  }
  return false;
}

/**
 * @param {string} url
 * @returns  {string}
 */
function cleanUrl(url) {
  const urlObj = new URL(url);
  const params = urlObj.searchParams;

  // Remove the `_gl` query parameter
  params.delete("_gl");

  // Reconstruct the URL without the `_gl` query parameter
  urlObj.search = params.toString();

  return decodeURIComponent(urlObj.toString()).replace(/'/g, "");
}

/**
 * @param {object} param0
 * @param {import("next").NextPageContext} [param0.nextPageContext]
 * @param {import("@splitfire-agency/raiden-library/dist/libraries/Uri").default} param0.baseUri
 * @param {string} [param0.locale]
 * @returns {Promise<void>}
 */
export default async function nextGetRedirectionsUrls({
  nextPageContext,
  baseUri,
  locale,
}) {
  if (shouldTestUrl(baseUri, nextPageContext)) {
    const uri = nextGetUri({ req: nextPageContext?.req });
    const supportedLocales =
      process.env.NEXT_PUBLIC_SUPPORTED_LOCALES?.split(",");
    let initialPath =
      supportedLocales.length > 1
        ? `${locale}\${uri.getPath()}`
        : uri.getPath();
    if (process.env.NEXT_PUBLIC_FRONT_BASE_PATH) {
      initialPath = `${process.env.NEXT_PUBLIC_FRONT_BASE_PATH}${initialPath}`;
    }
    const url = uri.setPath(initialPath).toString();

    const redirectionsUrlsData = await serverApiFetcher(
      generateApiUrl({
        id: "@api.redirections.urls.hit",
        query: {
          source_url: cleanUrl(url),
          environment_id: DEFAULT_ENVIRONMENT_ID,
        },
      }),
      undefined,
      {
        origin: baseUri.toString(),
        locale,
      },
    ).catch(function (tempError) {
      // on ne throw pas, on ne veut pas que l'app affiche une erreur
      console.error(JSON.stringify(tempError), "tempError");
    });

    /** @type {import("../../../types/redirection/Url").RedirectionUrl} */
    const redirectionsUrls = redirectionsUrlsData?.data;

    if (nextPageContext?.res && redirectionsUrls) {
      await nextRedirect({
        code:
          redirectionsUrls.redirection_type ===
          REDIRECTIONS_URLS_TIME_VALUE_PERMANENT
            ? 301
            : 302,
        redirectionUri: redirectionsUrls.target_url,
        res: nextPageContext?.res,
      });
    }
  }
}
